import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { post } from 'axios';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { callbackSvgs, actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import If from 'components/If';

import Divider from 'components/FormGroup/Divider';
import HourField from 'components/FormGroup/HourField';
import Textarea from 'components/FormGroup/Textarea';
import SelectField from 'components/FormGroup/SelectField';

import {
  formatSeconds,
  triggerHour,
  secondsToHours,
  formatDate,
} from 'utils/moment';

import history from 'utils/history';
import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';
import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import { getAppointmentStatus } from './utils';
import * as S from './styles';

const {
  alocatedProjectList,
  justificationsList,
  postAppointments,
  postAdminAppointment,
  getJustificationScope,
  deleteAppointment,
} = endpoints;

export default function AppointmentForm({
  reloadAppointments,
  resetSelections,
  currentAppointment,
  user,
  setLoading,
  setToastVisibility,
  setToastInfos,
  setOverlayVisibility,
  setHistoryVisibility,
  state,
}) {
  const {
    date,
    horasEmProjeto,
    totalHorasPonto,
    horasPonto,
    horas_extras: horasExtras,
    bloqueado,
  } = currentAppointment;

  const {
    id_funcionario: userID,
    bate_ponto: batePonto,
    horas_administrativas,
  } = user;
  const hasDiffentJourney = horas_administrativas === 0;

  const [appointments, setAppointments] = useState([]);
  const [extraHours, setExtraHours] = useState({});
  const [extraHourOptions, setExtraHourOptions] = useState([]);
  const [extraHourSubtopicOptions, setExtraHourSubtopicOptions] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [allocatedProjects, setAllocatedProjects] = useState([]);

  const [selectedAdminJustification, setSelectedAdminJustification] = useState(
    '',
  );
  const [adminJustifications, setAdminJustifications] = useState([]);

  const [allAllocations] = useFetch(
    `${alocatedProjectList}/${state?.id_funcionario ?? userID}/${formatDate(
      date,
      'YYYY-MM-DD',
    )}`,
  );
  const [appointmentJustifications] = useFetch(justificationsList);
  const [adminAppointmentJustifications] = useFetch(
    `${getJustificationScope}/apontamento/admin`,
  );

  useEffect(() => {
    (async () => {
      const projects = await allAllocations?.map(
        ({ id_projeto: id, nome, identificacao }) => ({
          value: id,
          label: `${nome} | ${identificacao}`,
        }),
      );
      const allJustifications = await appointmentJustifications?.map(
        ({ nome_categoria: nomeCategoria }) => ({
          value: nomeCategoria,
          label: nomeCategoria,
        }),
      );

      setAllocatedProjects(sortArrayInAlphabeticalOrder(projects, 'label'));
      setExtraHourOptions(allJustifications);
      setJustifications(appointmentJustifications);
    })();
  }, [allAllocations, appointmentJustifications]);

  useEffect(() => {
    (async () => {
      if (Object.keys(state || {}).length) {
        const allJustifications = adminAppointmentJustifications?.map(
          ({ descricao, id_justificativa: id }) => ({
            value: id,
            label: descricao,
          }),
        );

        setSelectedAdminJustification(
          horasEmProjeto?.[0]?.id_justificativa ?? null,
        );
        setAdminJustifications(allJustifications);
      }
    })();
  }, [
    state,
    adminAppointmentJustifications,
    currentAppointment,
    horasEmProjeto,
  ]);

  const getExtraHourSubtopic = useCallback(
    value => {
      const subtopic = justifications.filter(
        ({ nome_categoria: nomeCategoria }) => nomeCategoria === value,
      );
      const { subcategoria } = subtopic[0];

      const allSubtopics = subcategoria.map(
        ({ id_subcategoria_hora_extra: id, descricao }) => ({
          value: id,
          label: descricao,
        }),
      );
      setExtraHourSubtopicOptions(allSubtopics);
    },
    [justifications],
  );

  useEffect(() => {
    setExtraHours([]);
    setExtraHourOptions([]);
    setExtraHourSubtopicOptions([]);
    setAppointments(horasEmProjeto || []);

    if (horasExtras?.length) {
      setExtraHours(horasExtras[0]);
      getExtraHourSubtopic(horasExtras[0].categoria);
    }
  }, [horasEmProjeto, horasExtras, getExtraHourSubtopic]);

  const appointedHours = useMemo(
    () => _.sumBy(appointments, 'horas_apontadas'),
    [appointments],
  );

  const hoursLeft = useMemo(() => {
    if (totalHorasPonto > 0) {
      return appointedHours
        ? totalHorasPonto - appointedHours
        : totalHorasPonto;
    }

    return 0;
  }, [appointedHours, totalHorasPonto]);

  const insertedProjectsOptions = useMemo(() => {
    if (appointments.length > 0) {
      return appointments.map(({ nome, id_projeto: id }) => ({
        label: nome,
        value: id,
      }));
    }

    return [];
  }, [appointments]);

  const hasOddHours = useMemo(() => horasPonto?.length % 2 !== 0, [horasPonto]);

  const hasExtraHourPointed = useMemo(
    () => appointments.filter(({ nome }) => nome === extraHours?.nome_projeto),
    [appointments, extraHours],
  );

  const isAllAppointmentsApproved = useMemo(() => {
    const allApproveIds = appointments.map(
      ({ id_aprovacao: approveId }) => approveId,
    );

    if (allApproveIds.length <= 0) {
      return false;
    }

    return allApproveIds.every(approveId => approveId === 2);
  }, [appointments]);

  function validateLeftHours() {
    if (hoursLeft < 0 && batePonto === 1) {
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message:
          'A quantidade de horas apontadas é superior às horas trabalhadas',
        closeTime: null,
      });
    }
  }

  function handleHourInProjectChange(index, hour) {
    const project = {
      horas_apontadas: formatSeconds(hour),
    };

    setAppointments(currentAppointments =>
      currentAppointments.map((appointment, appointmentIndex) => {
        if (appointmentIndex === index) {
          return { ...appointments[index], ...project };
        }
        return appointment;
      }),
    );
  }

  function handleProjectSelection(label, value, hour, index) {
    const appointmentId = appointments[index].id_apontamento;

    const project = {
      nome: label,
      dt_apontamento: date,
      id_projeto: value,
      id_funcionario: state?.id_funcionario ?? userID,
      id_usr_in: userID,
      id_usr_up: userID,
      id_aprovacao: 1,
      id_apontamento: appointmentId ?? null,
      horas_apontadas: formatSeconds(hour),
    };

    if (Object.keys(state || {}).length) {
      project.id_funcionario_autor = userID;
      project.id_justificativa = selectedAdminJustification;
    }

    setAppointments(currentAppointments =>
      currentAppointments.map((appointment, appointmentIndex) => {
        if (appointmentIndex === index) {
          return { ...project, ...appointment[index] };
        }
        return appointment;
      }),
    );
  }

  function setLeftHoursInField(pointedHours, leftHours) {
    if (pointedHours) return secondsToHours(pointedHours);

    if (leftHours) return secondsToHours(leftHours);

    return '';
  }

  async function handleRemove(projectId, appointmentId) {
    setLoading(true);
    setAppointments(
      appointments.filter(({ id_projeto: id }) => id !== projectId),
    );

    if (appointmentId) {
      try {
        await post(deleteAppointment, {
          id_apontamento: appointmentId,
        });

        reloadAppointments(current => !current);

        setToastVisibility(true);
        setToastInfos({
          type: 'information',
          message: 'Apontamento excluído',
          closeTime: null,
        });
      } catch {
        setToastVisibility(true);
        setToastInfos({
          type: 'error',
          message: 'Erro ao excluir apontamento',
          closeTime: null,
        });
      }
    }

    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const allAppointments = appointments.map(appointment => {
        const currAppointment = appointment;

        if (Object.keys(state || {}).length) {
          currAppointment.id_funcionario = state.id_funcionario ?? userID;
          currAppointment.id_funcionario_autor = userID;
          currAppointment.id_justificativa = selectedAdminJustification;
        }

        delete currAppointment.descricao_justificativa;
        delete currAppointment.escopo_justificativa;
        delete currAppointment.tipo_justificativa;

        return {
          ...currAppointment,
          id_usr_up: userID,
        };
      });

      const objectToSend = {
        apontamentos: allAppointments,
      };

      if (
        !Object.keys(state || {}).length &&
        batePonto === 1 &&
        triggerHour(totalHorasPonto) >= 0
      ) {
        const extraHour = {
          id_funcionario: userID,
          id_usr_in: userID,
          id_usr_up: userID,
          id_projeto: extraHours.id_projeto ?? null,
          id_subcategoria_hora_extra: extraHours.id_sub_categoria ?? null,
          date,
          hora_extra: totalHorasPonto - 30600,
          observacao: extraHours.observacao ?? null,
        };

        objectToSend.hora_extra = extraHour;
      }

      if (Object.keys(state || {}).length) {
        await post(postAdminAppointment, objectToSend);
        setSelectedAdminJustification('');
      }

      if (!Object.keys(state || {}).length) {
        await post(postAppointments, objectToSend);
      }

      setLoading(false);
      setToastVisibility(true);
      setToastInfos({
        type: 'success',
        message: 'Apontamento realizado com sucesso',
        closeTime: null,
      });

      reloadAppointments(current => !current);
      resetSelections();
    } catch {
      setLoading(false);
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message: 'Erro ao realizar apontamento de horas',
        closeTime: null,
      });
    }
  }

  return (
    <>
      <If condition={!!Object.keys(state || {}).length}>
        <S.AppointmentByPersonContainer>
          <S.AppointmentByPersonTitle>
            Você está apontando horas por
          </S.AppointmentByPersonTitle>
          <S.PersonName>{state?.funcionario}</S.PersonName>
          <S.PersonFunction>
            <span>Função:</span>
            {state?.nome_funcao}
          </S.PersonFunction>
        </S.AppointmentByPersonContainer>
      </If>

      <S.Container hasState={!!Object.keys(state || {}).length}>
        <If condition={Object.keys(currentAppointment).length > 0}>
          <S.FormHeader>
            {`Apontamento - ${formatDate(date, 'DD/MM/YYYY')}`}
          </S.FormHeader>
          <S.FormHistory
            onClick={() => setHistoryVisibility(current => !current)}
          >
            Visualizar histórico deste dia
          </S.FormHistory>

          <If condition={!!Object.keys(state || {}).length}>
            <S.AdminJustification>
              <SelectField
                options={adminJustifications}
                handleChange={({ value }) => {
                  setSelectedAdminJustification(value);
                }}
                value={adminJustifications.find(
                  ({ value }) => value === selectedAdminJustification,
                )}
                isDisabled={isAllAppointmentsApproved}
                labelText="Justificativa do apontamento (*)"
                placeholderText="Qual o motivo deste apontamento?"
                hasLabelMargin
              />
            </S.AdminJustification>
          </If>

          <S.FormWrapper>
            {({ isSubmitting }) => (
              <S.Form onSubmit={handleSubmit}>
                <Divider text="Em qual(is) projeto(s) você trabalhou neste dia? E quantas horas você esteve em cada?" />

                <S.AppointmentsContainer>
                  {appointments.map((project, index) => {
                    const currentProject = allocatedProjects.find(({ label }) =>
                      label.includes(project.nome),
                    );
                    const pointedHours = setLeftHoursInField(
                      project.horas_apontadas,
                      hoursLeft,
                    );

                    return (
                      <S.AppointmentCard key={project.key ?? project.nome}>
                        <SelectField
                          name={`appointmentProject-${index + 1}`}
                          labelText="Nome do projeto (*)"
                          placeholderText="Selecione um projeto..."
                          options={allocatedProjects.filter(({ value }) => {
                            const pointedProjects = appointments.map(
                              ({ id_projeto: id }) => id,
                            );
                            return !pointedProjects.includes(value);
                          })}
                          value={currentProject || ''}
                          isDisabled={
                            (bloqueado && !Object.keys(state || {}).length) ||
                            project.id_aprovacao === 2
                          }
                          handleChange={({ value, label }) => {
                            handleProjectSelection(
                              label,
                              value,
                              pointedHours,
                              index,
                            );
                          }}
                        />
                        <HourField
                          name={`appointmentHours-${index + 1}`}
                          labelText="Horas trabalhadas (*)"
                          placeholderText="Quantidade de horas neste projeto"
                          value={pointedHours}
                          onBlur={validateLeftHours}
                          isDisabled={
                            (bloqueado && !Object.keys(state || {}).length) ||
                            project.id_aprovacao === 2
                          }
                          handleChange={e => {
                            const { value } = e.target;
                            handleHourInProjectChange(index, value);
                          }}
                        />

                        <If condition={!!project.id_apontamento}>
                          <S.AppointmentStatus>
                            Status de aprovação:
                            <S.Status status={project.id_aprovacao}>
                              {getAppointmentStatus(project.id_aprovacao)}
                            </S.Status>
                          </S.AppointmentStatus>
                        </If>

                        <S.AppointmentActions>
                          <If condition={project.id_aprovacao === 2}>
                            <FilledButton
                              buttonClass="confirm"
                              text="Solicitar reembolso"
                              handleClick={() => {
                                history.push({
                                  pathname:
                                    '/auth/appointments/request-refunds',
                                  state: {
                                    ...project,
                                    date,
                                    viewType: 'insert',
                                  },
                                });
                              }}
                              disabled={!!Object.keys(state || {}).length}
                            />
                          </If>

                          <If condition={project.id_aprovacao !== 2}>
                            <S.RemoveAppointment
                              type="button"
                              onClick={() => {
                                handleRemove(
                                  project.id_projeto,
                                  project.id_apontamento,
                                );
                              }}
                              disabled={bloqueado}
                            >
                              {actionSvgs('remove')}
                              Excluir
                            </S.RemoveAppointment>
                          </If>
                        </S.AppointmentActions>

                        <If
                          condition={
                            horasExtras?.length > 0 &&
                            extraHours.nome_projeto === project.nome
                          }
                        >
                          <S.AppointmentExtraHour>
                            <S.ExtraHourTitle>
                              Informações da hora extra neste apontamento
                            </S.ExtraHourTitle>

                            <S.ExtraHourItem>
                              <span>Categoria:</span>
                              {extraHours.categoria}
                            </S.ExtraHourItem>

                            <S.ExtraHourItem>
                              <span>Subcategoria:</span>
                              {extraHours.subcategoria}
                            </S.ExtraHourItem>

                            <S.ExtraHourItem>
                              <span>Observações:</span>
                              {extraHours.observacao ?? 'Sem observações'}
                            </S.ExtraHourItem>
                          </S.AppointmentExtraHour>
                        </If>
                      </S.AppointmentCard>
                    );
                  })}
                </S.AppointmentsContainer>

                <S.AddAppointment>
                  <FilledButton
                    text="+ Adicionar projeto"
                    handleClick={() => setAppointments([...appointments, {}])}
                    disabled={
                      (bloqueado && !Object.keys(state || {}).length) ||
                      (!!Object.keys(state || {}).length &&
                        !selectedAdminJustification) ||
                      ((hasOddHours || hoursLeft <= 0) && batePonto === 1)
                    }
                  />
                </S.AddAppointment>

                <If
                  condition={
                    batePonto === 1 &&
                    !Object.keys(state || {}).length &&
                    ((hasDiffentJourney &&
                      triggerHour(totalHorasPonto, '12:01') >= 0) ||
                      (!hasDiffentJourney &&
                        triggerHour(totalHorasPonto) >= 0)) &&
                    (!horasExtras.length || hasExtraHourPointed.length === 0)
                  }
                >
                  <S.ExtraHourContainer>
                    <Divider text="Dê-nos informações sobre as suas horas extras" />

                    <SelectField
                      name="extraHourProject"
                      labelText="Selecione um projeto para apontar suas horas extras (*)"
                      placeholderText="Escolha um projeto selecionado anteriormente..."
                      value={insertedProjectsOptions.find(({ label }) =>
                        label?.includes(extraHours?.nome_projeto),
                      )}
                      options={insertedProjectsOptions}
                      handleChange={({ label, value }) => {
                        setExtraHours({
                          ...extraHours,
                          nome_projeto: label,
                          id_projeto: value,
                        });
                      }}
                      isDisabled={bloqueado || !appointments.length > 0}
                    />

                    <SelectField
                      name="extraHourCategory"
                      hasLabelMargin
                      labelText="Especifique o motivo das suas horas extras (*)"
                      placeholderText="Selecione o motivo..."
                      value={extraHourOptions.find(
                        ({ label }) => label === extraHours.categoria,
                      )}
                      options={extraHourOptions}
                      handleChange={({ value, label }) => {
                        getExtraHourSubtopic(value);
                        setExtraHours({ ...extraHours, categoria: label });
                      }}
                      isDisabled={bloqueado || !extraHours?.nome_projeto}
                    />

                    <SelectField
                      name="extraHourSubtopic"
                      hasLabelMargin
                      labelText="Escolha a subcategoria deste motivo (*)"
                      placeholderText="Selecione a subcategoria..."
                      value={extraHourSubtopicOptions.find(
                        ({ value }) => value === extraHours.id_sub_categoria,
                      )}
                      options={extraHourSubtopicOptions}
                      isDisabled={
                        bloqueado || extraHourSubtopicOptions.length <= 0
                      }
                      handleChange={({ value, label }) => {
                        setExtraHours({
                          ...extraHours,
                          id_sub_categoria: value,
                          subcategoria: label,
                        });
                      }}
                    />

                    <Textarea
                      name="extraHourNote"
                      labelText="Se achar necessário, adicionar uma descrição à estas horas"
                      placeholderText="Insira sua mensagem..."
                      value={extraHours.observacao}
                      handleChange={({ target: { value } }) => {
                        setExtraHours({ ...extraHours, observacao: value });
                      }}
                      disabled={bloqueado}
                    />
                  </S.ExtraHourContainer>
                </If>

                <S.FormActions>
                  <FilledButton
                    buttonClass="cancel"
                    text="Cancelar"
                    handleClick={() => {
                      setOverlayVisibility(current => !current);
                    }}
                  />
                  <FilledButton
                    hasTooltip
                    tooltipPosition="top-left"
                    tooltipText={
                      bloqueado && !Object.keys(state || {}).length
                        ? 'O mês selecionado foi ENCERRADO pela empresa e não pode mais sofrer alterações'
                        : 'Aponte todas as suas horas efetivas de trabalho'
                    }
                    buttonType="submit"
                    buttonClass="confirm"
                    text="Apontar horas"
                    disabled={
                      (bloqueado && !Object.keys(state || {}).length) ||
                      (!!Object.keys(state || {}).length &&
                        !selectedAdminJustification) ||
                      isSubmitting ||
                      (isAllAppointmentsApproved &&
                        totalHorasPonto - appointedHours === 0) ||
                      (!!Object.keys(state || {}).length &&
                        hoursLeft !== 0 &&
                        batePonto === 1) ||
                      !appointments.length > 0 ||
                      (!Object.keys(state || {}).length &&
                        batePonto === 1 &&
                        (hasDiffentJourney
                          ? triggerHour(totalHorasPonto, '12:01')
                          : triggerHour(totalHorasPonto)) >= 0 &&
                        !extraHours?.subcategoria)
                    }
                  />
                </S.FormActions>
              </S.Form>
            )}
          </S.FormWrapper>
        </If>

        <If condition={Object.keys(currentAppointment).length <= 0}>
          <S.CallbackContainer>
            {callbackSvgs('womanWriting')}
            <S.CallbackText>
              Selecione um dia na tabela e aponte as suas horas
            </S.CallbackText>
          </S.CallbackContainer>
        </If>
      </S.Container>
    </>
  );
}

AppointmentForm.defaultProps = {
  user: {},
  currentAppointment: {},
  state: {},
};

AppointmentForm.propTypes = {
  reloadAppointments: PropTypes.func.isRequired,
  resetSelections: PropTypes.func.isRequired,
  currentAppointment: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  setLoading: PropTypes.func.isRequired,
  setToastVisibility: PropTypes.func.isRequired,
  setToastInfos: PropTypes.func.isRequired,
  setHistoryVisibility: PropTypes.func.isRequired,
  setOverlayVisibility: PropTypes.func.isRequired,
  state: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};
